import { useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';

import { useCurrentUser } from '@/hooks';
import { resolveInsightsViewerRole } from '@/utils';

import type { InsightsViewerRole } from '@/constants';
import type { PulseReportPageStatus } from '@/types';

import PulseReportPage from '@/components/Insights/PulseReport';
import {
  useGetOnboardingReviewQuery,
  useGetPulseReportQuery,
  useMarkPulseReportDebriefedMutation,
} from '@/store/api/pulseApi';
import {
  isOnboardingBaselineReport,
  mapPulseReportToPayload,
} from '@/utils/pulseReportAdapters';

type QueryError = {
  status?: number;
};

const resolveStatus = (
  reportId: string | undefined,
  isLoading: boolean,
  error: unknown,
  hasReport: boolean,
): PulseReportPageStatus => {
  if (!reportId) {
    return 'not_found';
  }
  if (isLoading) {
    return 'loading';
  }
  if (error) {
    const { status } = error as QueryError;
    if (status === 404) {
      return 'not_found';
    }
    if (status === 403) {
      return 'unauthorized';
    }
    return 'error';
  }
  if (!hasReport) {
    return 'not_found';
  }
  return 'ready';
};

type PulseReportRouteProps = {
  backTo: string;
  onboardingReviewRedirectTo?: (reportId: string) => string;
  redirectTo: string;
  rolloutEnabled: boolean;
  viewerRole: InsightsViewerRole;
};

const PulseReportRoute = ({
  backTo,
  onboardingReviewRedirectTo,
  redirectTo,
  rolloutEnabled,
  viewerRole,
}: PulseReportRouteProps) => {
  const { hash } = useLocation();
  const { reportId } = useParams<{ reportId?: string }>();
  const { data, error, isLoading, isUninitialized, refetch } =
    useGetPulseReportQuery(reportId || '', {
      skip: !reportId || !rolloutEnabled,
    });
  const [debriefError, setDebriefError] = useState<string>();
  const [markDebriefed, { isLoading: isMarkingDebriefed }] =
    useMarkPulseReportDebriefedMutation();
  const hasOnboardingBaselineMetadata = data?.report
    ? isOnboardingBaselineReport(data.report)
    : false;
  const shouldProbeOnboardingReview = Boolean(
    data?.report &&
    onboardingReviewRedirectTo &&
    !hasOnboardingBaselineMetadata,
  );
  const {
    data: onboardingReviewData,
    error: onboardingReviewError,
    isFetching: isOnboardingReviewFetching,
    isLoading: isOnboardingReviewLoading,
    refetch: refetchOnboardingReview,
  } = useGetOnboardingReviewQuery(reportId || '', {
    skip: !reportId || !shouldProbeOnboardingReview,
  });

  const reports = useMemo(
    () =>
      data
        ? [data.report, data.previousReport]
            .filter((report): report is NonNullable<typeof report> =>
              Boolean(report),
            )
            .map(mapPulseReportToPayload)
        : null,
    [data],
  );
  const report = reports?.[0] ?? null;
  const isResolvingOnboardingRedirect =
    shouldProbeOnboardingReview &&
    (isOnboardingReviewLoading || isOnboardingReviewFetching);
  const onboardingReviewErrorStatus = onboardingReviewError
    ? (onboardingReviewError as QueryError).status
    : undefined;
  const hasOnboardingReviewProbeError =
    shouldProbeOnboardingReview &&
    Boolean(onboardingReviewError) &&
    onboardingReviewErrorStatus !== 404;
  const status = resolveStatus(
    reportId,
    isLoading || isResolvingOnboardingRedirect,
    error ||
      (hasOnboardingReviewProbeError ? onboardingReviewError : undefined),
    Boolean(report),
  );

  useEffect(() => {
    if (!report?.debrief?.canMarkDebriefed) {
      setDebriefError(undefined);
    }
  }, [report?.debrief?.canMarkDebriefed]);

  const handleMarkDebriefed = async () => {
    if (!reportId) {
      return;
    }

    setDebriefError(undefined);

    try {
      await markDebriefed(reportId).unwrap();
    } catch {
      setDebriefError('Unable to mark this pulse report as debriefed.');
      if (!isUninitialized) {
        try {
          await refetch();
        } catch {
          // Ignore refetch failures after the query is skipped or unmounted.
        }
      }
    }
  };

  if (!rolloutEnabled) {
    return <Navigate to={redirectTo} replace />;
  }

  if (data?.report && onboardingReviewRedirectTo) {
    const onboardingReportId = hasOnboardingBaselineMetadata
      ? data.report.reportId
      : onboardingReviewData?.review.pulseReportId;

    if (onboardingReportId) {
      return (
        <Navigate
          to={`${onboardingReviewRedirectTo(onboardingReportId)}${hash}`}
          replace
        />
      );
    }
  }

  return (
    <PulseReportPage
      backTo={backTo}
      debriefError={debriefError}
      errorMessage="Unable to load pulse report right now."
      isMarkingDebriefed={isMarkingDebriefed}
      report={report}
      reports={reports}
      status={status}
      viewerRole={viewerRole}
      onMarkDebriefed={handleMarkDebriefed}
      onRetry={() => {
        if (reportId) {
          refetch();
          if (shouldProbeOnboardingReview) {
            refetchOnboardingReview();
          }
        }
      }}
    />
  );
};

const CoachMemberPulseReport = () => {
  const { id } = useParams<{ id?: string }>();
  const { user, isUserLoading } = useCurrentUser();

  if (isUserLoading) {
    return null;
  }

  return (
    <PulseReportRoute
      backTo={`/coach/members/${id || ''}/insights`}
      redirectTo={`/coach/members/${id || ''}/insights`}
      rolloutEnabled={!!user.insightsV2Enabled}
      viewerRole={resolveInsightsViewerRole(user)}
    />
  );
};

export const MemberSelfPulseReport = () => {
  const { user, isUserLoading } = useCurrentUser();

  if (isUserLoading) {
    return null;
  }

  return (
    <PulseReportRoute
      backTo="/m/insights"
      onboardingReviewRedirectTo={(reportId) =>
        `/m/onboarding-review/${reportId}`
      }
      redirectTo="/m/insights"
      rolloutEnabled={!!user.insightsV2Enabled}
      viewerRole="member"
    />
  );
};

export default CoachMemberPulseReport;
